import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chooser from '../../../../components/Chooser';
import USStateDropdown from '../../../../components/USStateDropdown';
import CountryDropdown from '../../../../components/CountryDropdown';
import { useCartMode } from '../../../../lib/cartModes';
import * as types from '../../../../types';
import styles from './Billing.module.scss';
import { useLocale } from '../../../../contexts/LocaleContext';

function ExistingAddressForm({ shippingAddress }: { shippingAddress: types.Address }) {
  return (
    <div key="shipping-address">
      <input type="hidden" data-recurly="address1" defaultValue={shippingAddress.line1} />
      <input type="hidden" data-recurly="address2" defaultValue={shippingAddress.line2 || ''} />
      <input type="hidden" data-recurly="city" defaultValue={shippingAddress.city} />
      <input type="hidden" data-recurly="state" defaultValue={shippingAddress.state} />
      <input type="hidden" data-recurly="postal_code" defaultValue={shippingAddress.zip} />
      <input type="hidden" value={shippingAddress.country || 'US'} data-recurly="country" />
    </div>
  );
}

export interface AddressErrorFields {
  street1?: boolean;
  city?: boolean;
  state?: boolean;
  postal_code?: boolean;
}

interface NewAddressFormProps {
  compactForm?: boolean;
  existingAddress?: types.Address;
  errorFields?: AddressErrorFields;
  onCountryChange?: (country: string) => void;
}

function NewAddressForm({ existingAddress, compactForm, errorFields, onCountryChange }: NewAddressFormProps) {
  const { t } = useTranslation();
  const { country: localeCountry } = useLocale();
  const [country, setCountry] = useState(localeCountry as string);
  const [state, setState] = useState(existingAddress?.state);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    setState(''); // Reset state when country changes
    onCountryChange?.(newCountry);
  };

  return (
    <div
      key="new-billing-address"
      className={classNames(styles.newBillingAddressForm, { [styles.compact]: compactForm })}
    >
      <input
        type="text"
        name="address1"
        className={errorFields?.street1 ? styles.error : ''}
        data-recurly="address1"
        defaultValue={existingAddress?.line1}
        placeholder={compactForm ? t('billing_address') : t('address_placeholder')}
        autoComplete="address-line1"
        data-hj-whitelist
      />
      <input
        type="text"
        name="address2"
        data-recurly="address2"
        defaultValue={existingAddress?.line2 || undefined}
        placeholder={t('apt_suite_other_optional')}
        autoComplete="address-line2"
        data-hj-whitelist
      />
      <input
        type="text"
        data-recurly="city"
        name="city"
        className={errorFields?.city ? styles.error : ''}
        defaultValue={existingAddress?.city}
        placeholder={t('city')}
        autoComplete="address-level2"
        data-hj-whitelist
      />
      <div className={styles.formRow}>
        <div>
          <USStateDropdown
            state={state}
            onChange={(newState) => setState(newState)}
            error={errorFields?.state ?? false}
            country={country}
          />
          <input type="hidden" data-recurly="state" value={state} />
        </div>
        <div>
          <input
            type="text"
            name="zip"
            data-recurly="postal_code"
            defaultValue={existingAddress?.zip}
            placeholder={t('zip_code')}
            autoComplete="postal-code"
            className={classNames('zip-code', errorFields?.postal_code ? styles.error : '')}
            data-hj-whitelist
          />
        </div>
      </div>
      <CountryDropdown country={country} onChange={handleCountryChange} />
      <input type="hidden" data-recurly="country" value={country} />
    </div>
  );
}

interface AddressFormProps {
  shippingAddress: types.Address | undefined;
  useShippingAddress: boolean;
  billingInfo?: types.BillingInfo | null;
  compactForm?: boolean;
  errorFields?: AddressErrorFields;
  onCountryChange?: (country: string) => void;
}

function AddressForm({
  shippingAddress,
  billingInfo,
  useShippingAddress,
  compactForm,
  errorFields,
  onCountryChange,
}: AddressFormProps) {
  const { checkoutContext } = useCartMode();

  if (useShippingAddress && shippingAddress) {
    return <ExistingAddressForm shippingAddress={shippingAddress} />;
  }

  // The ExistingAddressForm assumes a user has recently entered a shipping address,
  // but this is not the case in the context of managing one's subscription, so here
  // we surface a NewAddressForm, which displays a full address form prepopulated
  // with existing billing information.
  if (checkoutContext === types.PaymentContext.MANAGE_SUBSCRIPTION) {
    return (
      <NewAddressForm
        existingAddress={billingInfo?.address}
        compactForm={compactForm}
        errorFields={errorFields}
        onCountryChange={onCountryChange}
      />
    );
  }
  return <NewAddressForm compactForm={compactForm} errorFields={errorFields} onCountryChange={onCountryChange} />;
}

interface BillingAddressProps {
  shippingAddress: types.Address | undefined;
  compactForm?: boolean;
  billingInfo?: types.BillingInfo | null;
  errorFields?: AddressErrorFields;
  onCountryChange?: (country: string) => void;
}

export default function BillingAddress({
  shippingAddress,
  compactForm,
  billingInfo,
  errorFields,
  onCountryChange,
}: BillingAddressProps) {
  const { t } = useTranslation();
  const [useShippingAddress, setUseShippingAddress] = useState(!!shippingAddress);

  return (
    <div className={styles.formSection}>
      {!compactForm && <h4>{t('billing_address')}</h4>}
      {shippingAddress && (
        <Chooser
          onSelect={(useShippingAddressChoice) => setUseShippingAddress(useShippingAddressChoice)}
          selectedOption={useShippingAddress}
          groups={[
            {
              options: [
                {
                  content: t('same_as_shipping_address'),
                  value: true,
                },
                {
                  content: t('use_a_different_address'),
                  value: false,
                },
              ],
            },
          ]}
          showRadio
          compact
        />
      )}
      <AddressForm
        shippingAddress={shippingAddress}
        billingInfo={billingInfo}
        useShippingAddress={useShippingAddress}
        compactForm={compactForm}
        errorFields={errorFields}
        onCountryChange={onCountryChange}
      />
    </div>
  );
}
